// components/cart/CartItem.js
import React from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';

const CartItemContainer = tw.div`flex flex-col bg-white w-full rounded-lg p-4 shadow mb-4`;
const ContentWrapper = tw.div`flex flex-row items-center w-full mb-3`;
const Image = tw.img`w-20 h-20 rounded mr-4`;
const Info = tw.div`flex-1`;
const Name = tw.h4`text-lg font-bold`;
const Price = tw.p`text-gray-600`;
const FinalPrice = tw.p`text-lg text-gray-700`;
const OriginalPrice = styled.p`
	${tw`line-through text-red-500`}
`;
const DiscountContainer = tw.div`flex items-center gap-2`;
const DiscountInfo = tw.p`text-sm text-green-600`;
const PackInfo = tw.div`text-sm text-gray-600 mt-1`;
const Actions = tw.div`flex items-center justify-between w-full`;
const Quantity = tw.div`flex items-center space-x-2`;
const Button = tw.button`px-3 py-1 bg-gray-200 rounded hover:bg-gray-300`;

const CartItem = ({
	item,
	handleIncrement,
	handleDecrement,
	handleQuitarCarrito,
	calcularSubtotalItem,
	obtenerDescuentos,
	calcularPrecioConDescuento,
}) => {
	const descuentos = obtenerDescuentos(item);

	// Lógica diferente para packs vs productos
	if (item.tipo === 'pack') {
		const tieneSnapshot = item.precioConAdicionalesCarrito != null;
		const precioBase =
			item.precioConAdicionalesCarrito ?? item.precioConAdicionales ?? item.precio_final ?? item.precio;
		const precioConDescuento = tieneSnapshot
			? Number(precioBase)
			: calcularPrecioConDescuento(precioBase, descuentos);
		const descuentoAplicado = precioBase - precioConDescuento;

		// Calcular costo de adicionales
		const costoAdicionales =
			item.precioConAdicionalesCarrito != null && item.precioUnitarioCarrito != null
				? Number(item.precioConAdicionalesCarrito) - Number(item.precioUnitarioCarrito)
				: item.precioConAdicionales
					? item.precioConAdicionales - (item.precio_final || item.precio)
					: 0;

		return (
			<CartItemContainer>
				<ContentWrapper>
					<Image
						src={
							item.imagen ||
							'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=500&q=80'
						}
						alt={item.nombre || item.titulo}
					/>
					<Info>
						<Name>{item.nombre || item.titulo}</Name>
						<PackInfo>Pack con {item.productos?.length || 0} productos</PackInfo>

						{/* Mostrar adicionales seleccionados */}
						{costoAdicionales > 0 && <Price>Adicionales: (+${costoAdicionales.toFixed(2)})</Price>}

						{/* Mostrar descuentos adicionales aplicados */}
						{descuentoAplicado > 0 && (
							<DiscountInfo>Descuento adicional: ${descuentoAplicado.toFixed(2)}</DiscountInfo>
						)}

						<FinalPrice>Total: ${calcularSubtotalItem(item).toFixed(2)}</FinalPrice>
					</Info>
				</ContentWrapper>
				<Actions>
					<Quantity>
						<Button onClick={() => handleDecrement(item)}>-</Button>
						<span>{item.cantidad}</span>
						<Button onClick={() => handleIncrement(item)}>+</Button>
					</Quantity>
					<Button onClick={() => handleQuitarCarrito(item)} tw="bg-red-500 text-white hover:bg-red-700">
						Quitar
					</Button>
				</Actions>
			</CartItemContainer>
		);
	} else if (item.tipo === 'vianda') {
		return (
			<CartItemContainer>
				<ContentWrapper>
					<Image
						src={
							item.imageSrc ||
							'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=500&q=80'
						}
						alt={item.titulo}
					/>
					<Info>
						<Name>{item.titulo}</Name>
						<Price>
							Plan: {item.cantidadDias || 0} días | {item.viandasDiarias || 1} vianda(s) por día
						</Price>
						<Price>Precio pack: ${Number(item.precioUnitarioCarrito ?? item.precio ?? 0).toFixed(2)}</Price>
						<FinalPrice>Total: ${calcularSubtotalItem(item).toFixed(2)}</FinalPrice>
					</Info>
				</ContentWrapper>
				<Actions>
					<Quantity>
						<Button onClick={() => handleDecrement(item)}>-</Button>
						<span>{item.cantidad}</span>
						<Button onClick={() => handleIncrement(item)}>+</Button>
					</Quantity>
					<Button onClick={() => handleQuitarCarrito(item)} tw="bg-red-500 text-white hover:bg-red-700">
						Quitar
					</Button>
				</Actions>
			</CartItemContainer>
		);
	} else {
		// Lógica original para productos individuales
		const precioConDescuento =
			item.precioUnitarioCarrito != null
				? Number(item.precioUnitarioCarrito)
				: calcularPrecioConDescuento(item.precio, descuentos);
		const descuentoAplicado =
			Number(item.precioListaUnitarioCarrito ?? item.precio ?? 0) - precioConDescuento;

		return (
			<CartItemContainer>
				<ContentWrapper>
					<Image
						src={
							item.imageSrc ||
							'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=500&q=80'
						}
						alt={item.titulo}
					/>
					<Info>
						<Name>{item.titulo}</Name>
						{descuentoAplicado > 0 && (
							<DiscountContainer>
							<OriginalPrice>
								${Number(item.precioListaUnitarioCarrito ?? item.precio ?? 0).toFixed(2)}
							</OriginalPrice>
								<DiscountInfo>Descuento: ${descuentoAplicado.toFixed(2)}</DiscountInfo>
							</DiscountContainer>
						)}
						<Price>Sub total: ${precioConDescuento.toFixed(2)}</Price>
						{item.adicional && (
							<Price>
							Adicional: {item.adicional.descripcion} (+$
							{Number(item.adicional.costoAdicionalCarrito ?? item.adicional.costoAdicional ?? 0).toFixed(2)})
							</Price>
						)}
						<FinalPrice>Total: ${calcularSubtotalItem(item).toFixed(2)}</FinalPrice>
					</Info>
				</ContentWrapper>
				<Actions>
					<Quantity>
						<Button onClick={() => handleDecrement(item)}>-</Button>
						<span>{item.cantidad}</span>
						<Button onClick={() => handleIncrement(item)}>+</Button>
					</Quantity>
					<Button onClick={() => handleQuitarCarrito(item)} tw="bg-red-500 text-white hover:bg-red-700">
						Quitar
					</Button>
				</Actions>
			</CartItemContainer>
		);
	}
};

export default CartItem;
